import { createNotification } from './notification';
import { MenuItem } from './LivefireCard/CardMenu';
import { type DisplayErrorProps } from './components/DisplayError';
import { type FeedItem, LivefireApi } from '@/classes';
import { Environment, LivefireCardType, livefireItemTypes } from '@/constants';
import { StyledComponent } from '@/helpers';
import { type ImageEmptyStateProps } from '@/layouts';

type FilterStateChangeCallback = {
  (settings: FilterState): void;
};

export type OrganismState = {
  error: LivefireError | null;
  status: LivefireStatusType;
  scrollPosition: 'top' | 'scrolled';
  firstTooltipVisible: boolean;
  api: LivefireApi | null;
};

export type LivefireStatus = {
  api: LivefireApi;
  appName: string;
  authToken: string | null;
  cardTypes: LivefireApi['cardTypes'];
  clientId: string | null;
  subscriptionId: string;
  subscriptionStatus: string;
  userName: string;
};

export type ViewState = {
  filterButtonActive: boolean;
  filtered: boolean;
  filteredItems: FeedItem[];
  itemTypeFilterEmptiedList: boolean;
  searchTerm: string;
  selectedItemTypes: Partial<Record<UIFilterStateKey, boolean>>;
  showFilterOptions: boolean;
  showHeader: boolean;
};

/**
 * Overall state flag for the Livefire organism
 */
export const status = {
  INITIALIZING: 'INITIALIZING',
  LOAD_FEED: 'LOAD_FEED',
  LOADING_FEED: 'LOADING_FEED',
  LOADED: 'LOADED',
  ERROR: 'ERROR',
  RETRY_ERROR: 'RETRY_ERROR',
} as const;

export type LivefireStatusType = (typeof status)[keyof typeof status];

/**
 * Error flags for the Livefire organism
 */
export const errors = {
  ACCOUNT_TIMEOUT: 'ACCOUNT_TIMEOUT',
  ACCOUNT_ERROR: 'ACCOUNT_ERROR',
  FEED_ERROR: 'FEED_ERROR',
  NOT_LOGGED_IN: 'NOT_LOGGED_IN',
} as const;

export type LivefireError = (typeof errors)[keyof typeof errors];

export type FilterStateKey = livefireItemTypes.FilterStateKey;
export type FilterState = livefireItemTypes.FilterState;

/** FilterStateKeys except play & insert_video which are not UI filters */
const validUIFilterStateKeys = [
  livefireItemTypes.open,
  livefireItemTypes.videoWatched,
  livefireItemTypes.click,
  livefireItemTypes.sent,
  livefireItemTypes.like,
  livefireItemTypes.comment,
  livefireItemTypes.replyWithVideo,
] as const;
export type UIFilterStateKey = (typeof validUIFilterStateKeys)[number];

export function isUIFilterStateKey(key: FilterStateKey): key is UIFilterStateKey {
  return validUIFilterStateKeys.includes(key as UIFilterStateKey);
}

export type EventHandlerContext = {
  api: LivefireApi;
  createNotification: typeof createNotification;
  setFeedItems: React.Dispatch<React.SetStateAction<FeedItem[]>>;
  setNotification: React.Dispatch<React.SetStateAction<ReturnType<typeof createNotification>>>;
};

/**
 * Livefire Props
 *
 * Supports "isRebrand" via Emotion's `ThemeProvider`.
 */
export type LivefireProps = StyledComponent & {
  /** Implementors can specify their own API object or use the default */
  api?: LivefireApi;
  /**
   * Livefire metric "app name"
   * @deprecated use MetroContext
   */
  appName?: string;
  /** users auth token */
  authToken?: string | null;
  /** Types of livefire cards to show. */
  cardTypes?: LivefireApi['cardTypes'];
  /** Client ID for this feed. Optional, can get it from token if necessary. */
  clientId?: string | null;
  /** Environment for LF */
  environment?: Environment | null;
  /**
   * Optional. Provides the current filter settings for the filter. Can be changed after feed is
   * loaded. Only used if onFilterStateChange is provided as well and must be stable between renders
   * if the value hasn't changed. If the filter is changed, a new object must be passed (object
   * identity equality).
   */
  filterState?: Partial<Record<UIFilterStateKey, boolean>>;
  /** Feed item menu configuration */
  menuConfig?: {
    cardMenus?: Partial<Record<LivefireCardType, MenuItem[]>>;
    onMenuItemClick?: (selectedMenu: string, item: FeedItem) => void;
  };
  /** Notifications banner */
  notification?: ReturnType<typeof createNotification>;
  /** Button click handler for "no feed items" screen */
  onEmptyStateClick?: React.MouseEventHandler;
  /** Button click handler for any error screens */
  onErrorClick?: React.MouseEventHandler;
  /**
   * Optional. Called when the filter state is changed from within the component. After this method
   * is called the new state must be passed in via the filterState prop. This method _must_ be
   * stable, or unnecessary renders will occur. If not provided, an internal state will be
   * maintained to allow the filters to work.
   */
  onFilterStateChange?: FilterStateChangeCallback | null;
  /**
   * A function to handle metrics fired by components within the
   * Livefire feed.
   * @deprecated
   */
  onMetricTriggered?: VoidFunction | null;
  /** Handle URL open requests. Receives a URL to open. */
  onOpenUrlAction?: (url: string) => void;
  /** Handle changes to the search term. */
  onSearchTermChange?: (searchTerm: string) => void;
  /** The current search term, if available. */
  searchTerm?: string;
  /** Flag indicating whether filter options should be made visible. */
  showFilterOptions?: boolean;
  /**
   * Flag indicating whether to show the livefire header or not. Implies that `showFilterOptions`
   * should also be false as there's no way to show the filter options if the header is not visible.
   */
  showHeader?: boolean;
  /** Client's subscription ID @deprecated */
  subscriptionId?: string;
  /** Client's subscription status @deprecated */
  subscriptionStatus?: string;
  /** Rate at which to update existing cards. Set to 0 to disable update. Default is 10s. */
  updateInterval?: number;
  /** User's name used for metrics. @deprecated */
  userName?: string;
};

type ErrorAction = { type: 'error'; payload: { error: NonNullable<OrganismState['error']> } };
type ResetAction = { type: 'reset' | 'resetError' };
export type ScrollAction = {
  type: 'scroll';
  payload: { position: OrganismState['scrollPosition'] };
};
type StatusAction = {
  type: 'status';
  payload: { status: OrganismState['status']; resetError?: boolean };
};
type ToolTipAction = { type: 'tooltip'; payload: { position: number; visible: boolean } };
export type StatusActions = ErrorAction | ResetAction | ScrollAction | StatusAction | ToolTipAction;

// The order here is important, it determines the display on the UI.
export const itemTypeToUIMap: Readonly<Record<UIFilterStateKey, string>> = {
  [livefireItemTypes.open]: 'Opens',
  [livefireItemTypes.videoWatched]: 'Plays',
  [livefireItemTypes.click]: 'Clicks',
  [livefireItemTypes.sent]: 'Sends',
  [livefireItemTypes.like]: 'Likes',
  [livefireItemTypes.comment]: 'Comments',
  [livefireItemTypes.replyWithVideo]: 'Video Replies',
};

export type CreateLivefireCardCallbacks = {
  onOpenUrlAction: (url: string) => void;
  onEmptyStateClick: ImageEmptyStateProps['onButtonClick'];
  onErrorClick: DisplayErrorProps['onErrorClick'];
};

export const DISABLED_COUNT_TYPES: Readonly<LivefireCardType[]> = [
  livefireItemTypes.sent,
  livefireItemTypes.comment,
  livefireItemTypes.replyWithVideo,
];
